/* ============================================================
   CRISTAL ANTIGUO — SHOPIFY THEME JS
   ============================================================ */

/* ── Strings i18n ──────────────────────────────────────────────
   Textos del JS traducibles: los define el layout en window.theme.strings
   (desde locales). Aquí solo dejamos fallbacks por si faltara alguno. */
const THEME_STRINGS = Object.assign({
  cartTitle: 'Carrito',
  cartEmpty: 'Tu carrito está vacío.',
  continueShopping: 'Seguir comprando',
  checkout: 'Ir al pago',
  total: 'Total',
  securePayment: 'Pago seguro con Shopify',
  remove: 'Quitar',
  addToCart: 'Añadir al carrito',
  adding: 'Añadiendo…',
  soldOut: 'Vendido',
  priceLabel: 'Precio:',
  setOf: '/set de',
  quantity: 'Cantidad',
  cartItems: 'Productos en el carrito'
}, (window.theme && window.theme.strings) || {});

/* ── Utilities ─────────────────────────────────────────────── */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return amount + ' €';
}

// Formato de moneda para la barra de envío: sin decimales si es entero.
// ES: «105 €» · EN: «€105».
function formatShipMoney(cents, locale) {
  const eur = cents / 100;
  const whole = Math.round(eur) === eur;
  if (locale === 'en') {
    return '€' + (whole ? String(eur) : eur.toFixed(2));
  }
  return (whole ? String(eur) : eur.toFixed(2).replace('.', ',')) + ' €';
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/* ── CartPanel ──────────────────────────────────────────────── */
class CartPanel {
  constructor() {
    this.panel = document.querySelector('.cart-panel');
    this.overlay = document.querySelector('.cart-overlay');
    this.countEls = document.querySelectorAll('.cristal-cart-count');

    if (!this.panel) return;

    // Prioridad: ajustes de la sección (data-attrs, traducibles en T&A → Tema);
    // si faltan, base de locales (THEME_STRINGS).
    this.labels = {
      title: this.panel.dataset.title || THEME_STRINGS.cartTitle,
      emptyMessage: this.panel.dataset.emptyMessage || THEME_STRINGS.cartEmpty,
      continueShoppingText: this.panel.dataset.continueShoppingText || THEME_STRINGS.continueShopping,
      continueShoppingUrl: this.panel.dataset.continueShoppingUrl || '/collections/all',
      checkoutText: this.panel.dataset.checkoutText || THEME_STRINGS.checkout,
      totalText: this.panel.dataset.totalText || THEME_STRINGS.total,
      removeText: this.panel.dataset.removeText || THEME_STRINGS.remove,
      securePaymentText: this.panel.dataset.securePaymentText || THEME_STRINGS.securePayment,
    };

    // Barra de envío gratis: contexto calculado en Liquid (país + ajustes).
    this.ship = {
      show: this.panel.dataset.shipShow === 'true',
      threshold: parseInt(this.panel.dataset.shipThreshold, 10) || 0,
      lowcost: this.panel.dataset.shipLowcost ? parseInt(this.panel.dataset.shipLowcost, 10) : 0,
      highcost: this.panel.dataset.shipHighcost ? parseInt(this.panel.dataset.shipHighcost, 10) : 0,
      weightBreak: this.panel.dataset.shipWeightBreak ? parseInt(this.panel.dataset.shipWeightBreak, 10) : 1000,
      estimateNote: this.panel.dataset.shipEstimateNote || '',
      locale: this.panel.dataset.shipLocale || (document.documentElement.lang || 'es').slice(0, 2),
      label: this.panel.dataset.shipLabel || '',
      freeLabel: this.panel.dataset.shipFreeLabel || '',
      remainingTpl: this.panel.dataset.shipRemainingTpl || '',
      fromTpl: this.panel.dataset.shipFromTpl || '',
      cutoff: this.panel.dataset.shipCutoff ? parseInt(this.panel.dataset.shipCutoff, 10) : 0,
      subtotalText: this.panel.dataset.subtotalText || 'Subtotal',
    };

    // Recomendaciones «Completa tu pedido».
    this.recsLabels = {
      title: this.panel.dataset.recsTitle || '',
      add: this.panel.dataset.recsAdd || '',
    };
    // Subtotal (en céntimos) a partir del cual se sugieren piezas para completar el
    // envío gratis; por debajo, piezas de precio parecido a lo que ya lleva.
    this.recsCompleteFrom = this.panel.dataset.recsCompleteFrom ? parseInt(this.panel.dataset.recsCompleteFrom, 10) : 0;
    try {
      const recsEl = document.getElementById('cart-recs-data');
      this.recs = recsEl ? JSON.parse(recsEl.textContent) : [];
    } catch (e) {
      this.recs = [];
    }
    // Precalentamos en segundo plano las miniaturas más probables para que ya
    // estén en caché cuando se abra el carrito (evita el parpadeo al cargarlas).
    this.prefetchRecImages();
    // Si estamos en una ficha de producto, registramos la pieza como «vista».
    this.recordProductView();

    // Texto del banner (announcement) para mostrarlo dentro del carrito.
    const annTpl = document.getElementById('cart-announcement-tpl');
    this.announcementHtml = annTpl ? annTpl.innerHTML.trim() : '';

    this.bindEvents();
    this.refreshCart();
    this.maybeAutoOpen();
  }

  // Abre el minicart automáticamente si llegamos con ?cart=open
  // (p. ej. tras redirigir la antigua página /cart a la landing).
  maybeAutoOpen() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cart') !== 'open') return;
      this.open();
      params.delete('cart');
      const qs = params.toString();
      const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.history.replaceState({}, '', url);
    } catch (e) {
      console.error('[CartPanel] maybeAutoOpen error:', e);
    }
  }

  bindEvents() {
    document.querySelectorAll('[data-cart-open], .cart-trigger').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    const closeBtn = this.panel.querySelector('.cart-panel__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // «Atrás» del navegador: cierra el carrito en lugar de navegar.
    window.addEventListener('popstate', () => {
      if (this.panel.classList.contains('cart-panel--open')) this.close(true);
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-add-to-cart-form]');
      if (!form) return;
      e.preventDefault();
      this.addItem(form);
    });
  }

  open() {
    if (this.panel.classList.contains('cart-panel--open')) return;
    this.panel.classList.add('cart-panel--open');
    if (this.overlay) this.overlay.classList.add('cart-overlay--open');
    document.body.style.overflow = 'hidden';
    // Historial: añadimos un estado para que «atrás» cierre el carrito en vez de
    // salir de la página (sobre todo en móvil, donde ocupa toda la pantalla).
    try { history.pushState({ cartOpen: true }, ''); } catch (e) {}
    this.refreshCart();
  }

  // fromPopstate = true cuando el cierre viene del botón «atrás»; en ese caso el
  // navegador ya ha consumido el estado y no debemos retroceder otra vez.
  close(fromPopstate) {
    if (!this.panel.classList.contains('cart-panel--open')) return;
    this.panel.classList.remove('cart-panel--open');
    if (this.overlay) this.overlay.classList.remove('cart-overlay--open');
    document.body.style.overflow = '';
    if (!fromPopstate) {
      try { if (history.state && history.state.cartOpen) history.back(); } catch (e) {}
    }
  }

  async addItem(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = THEME_STRINGS.adding;
    }

    const data = new FormData(form);
    const body = {
      id: data.get('id'),
      quantity: parseInt(data.get('quantity') || 1, 10),
    };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error adding to cart');
      await this.refreshCart();
      this.open();
    } catch (err) {
      console.error('[CartPanel] addItem error:', err);
    } finally {
      // Siempre restaurar el botón (antes se quedaba en «Añadiendo…» al cerrar).
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel || THEME_STRINGS.addToCart;
      }
    }
  }

  async refreshCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      this.renderCart(cart);
      if (this.countEls.length) {
        const countText = cart.item_count > 0 ? ` (${cart.item_count})` : '';
        this.countEls.forEach((el) => { el.textContent = countText; });
      }
    } catch (err) {
      console.error('[CartPanel] refreshCart error:', err);
    }
  }

  // Resumen del carrito: Subtotal, línea de envío (Nacional/Internacional con su
  // coste y barra de progreso) y Total (subtotal + envío de la zona). Se
  // recalcula en cada render (añadir/quitar/cambiar), sin recargar.
  summaryHtml(cart) {
    const s = this.ship;
    const subtotal = cart.total_price;
    const cartWeight = cart.total_weight || 0;
    let shipRows = '';
    let shipCost = 0;
    let estimated = false;
    if (s.show && s.threshold) {
      const remaining = s.threshold - subtotal;
      if (remaining <= 0) {
        // Envío gratis alcanzado.
        shipRows = `<div class="cart-panel__row"><span>${escapeHtml(s.label)}</span><span>${escapeHtml(s.freeLabel)}</span></div>`;
      } else {
        // Coste de envío exacto según el peso del carrito (tramo ≤ corte → tarifa baja).
        if (s.lowcost && s.highcost) {
          shipCost = cartWeight <= s.weightBreak ? s.lowcost : s.highcost;
          estimated = true;
        }
        const pct = Math.max(0, Math.min(100, (subtotal / s.threshold) * 100));
        // Cerca del envío gratis (≤ cutoff): «Te faltan X €». Lejos: «Envío gratis a partir de X €».
        let shipText;
        if (s.fromTpl && s.cutoff && remaining > s.cutoff) {
          shipText = s.fromTpl.replace('[amount]', formatShipMoney(s.threshold, s.locale));
        } else {
          shipText = s.remainingTpl.replace('[amount]', formatShipMoney(remaining, s.locale));
        }
        shipRows = `
          <div class="cart-panel__row"><span>${escapeHtml(s.label)}</span><span>${formatMoney(shipCost)}</span></div>
          <p class="cart-ship__text">${escapeHtml(shipText)}</p>
          <div class="cart-ship__bar"><div class="cart-ship__fill" style="width:${pct}%"></div></div>`;
      }
    }
    const total = subtotal + shipCost;
    // Nota: el envío es estimado (el importe final se confirma en el checkout).
    const estimateNote = estimated && s.estimateNote
      ? `<p class="cart-ship__note">${escapeHtml(s.estimateNote)}</p>`
      : '';
    return `
      <div class="cart-panel__summary">
        <div class="cart-panel__row"><span>${escapeHtml(s.subtotalText)}</span><span>${formatMoney(subtotal)}</span></div>
        ${shipRows}
        <div class="cart-panel__row cart-panel__row--total"><span>${escapeHtml(this.labels.totalText)}</span><span>${formatMoney(total)}</span></div>
        ${estimateNote}
      </div>`;
  }

  // Elige las piezas a sugerir según el gasto del cliente:
  //  - Carrito pequeño (subtotal < umbral «completar»): best sellers en una
  //    horquilla de precio alrededor de lo que ya lleva (por encima, por debajo
  //    y mismo precio), no solo el mismo precio.
  //  - Carrito ya alto: piezas que cuesten lo que falta o un poco más, de la más
  //    ajustada a la más cara, para alcanzar el envío gratis sin pasarse.
  //  - Envío ya gratis: best sellers económicos (compra por impulso fácil).
  //  - Carrito vacío: best sellers.
  pickRecs(cart) {
    if (!this.recs || !this.recs.length) return [];
    const inCart = new Set((cart.items || []).map(i => i.variant_id || i.id));
    const pool = this.recs.filter(p => !inCart.has(p.id));
    let remaining = 0;
    if (this.ship.show && this.ship.threshold) remaining = this.ship.threshold - cart.total_price;
    let picks;
    if (remaining > 0 && cart.item_count > 0) {
      if (this.recsCompleteFrom && cart.total_price < this.recsCompleteFrom) {
        // Impulso: best sellers económicos (la mitad más barata de la colección),
        // con algo de aleatoriedad para que roten y ver cuáles enganchan más.
        const byPrice = pool.slice().sort((a, b) => a.price - b.price);
        const cheapCount = Math.max(6, Math.ceil(byPrice.length / 2));
        picks = this.shuffle(byPrice.slice(0, cheapCount));
      } else {
        // Piezas que alcancen lo que falta, de la más barata a la más cara.
        const reach = pool.filter(p => p.price >= remaining).sort((a, b) => a.price - b.price);
        picks = reach.length ? reach : pool.slice().sort((a, b) => b.price - a.price);
      }
    } else if (cart.item_count > 0) {
      // Envío ya gratis: las más baratas de las más vendidas (añadido fácil y mono).
      picks = pool.slice().sort((a, b) => a.price - b.price);
    } else {
      picks = pool; // carrito vacío: best sellers (orden de la colección)
    }
    // Prioridad a piezas vistas recientemente, pero solo si ya encajan en el
    // contexto (es decir, si están entre las candidatas ya filtradas por precio).
    const recentIds = this.recentlyViewedIds();
    if (recentIds.length) {
      const byId = new Map(picks.map(p => [p.id, p]));
      const front = [];
      recentIds.forEach(rid => { if (byId.has(rid)) { front.push(byId.get(rid)); byId.delete(rid); } });
      if (front.length) picks = front.concat(picks.filter(p => byId.has(p.id)));
    }
    // Variedad: no amontonar categorías que ya están en el carrito ni repetirlas
    // entre las recomendaciones, respetando el orden ya calculado.
    const cartTypes = new Set((cart.items || []).map(i => (i.product_type || '').toLowerCase()).filter(Boolean));
    return this.diversify(picks, cartTypes, 3);
  }

  // Guarda la pieza de la ficha actual como «vista recientemente» (ids de
  // variante, más reciente primero, máx. 12). Solo se registra si está disponible.
  recordProductView() {
    try {
      const el = document.getElementById('cristal-product-view');
      if (!el) return;
      const id = parseInt(el.textContent, 10);
      if (!id) return;
      const key = 'cristal_recent_v1';
      let list = [];
      try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }
      list = list.filter(x => x !== id);
      list.unshift(id);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (e) {}
  }

  recentlyViewedIds() {
    try { return JSON.parse(localStorage.getItem('cristal_recent_v1') || '[]'); } catch (e) { return []; }
  }

  // Baraja una copia del array (Fisher-Yates), sin tocar el original.
  shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  // Elige hasta n piezas priorizando categorías distintas a las del carrito y sin
  // repetir categoría entre ellas; si no llega, va relajando la restricción.
  diversify(sorted, cartTypes, n) {
    const chosen = [];
    const usedTypes = new Set();
    const take = (p) => { chosen.push(p); const t = (p.type || '').toLowerCase(); if (t) usedTypes.add(t); };
    // 1) categoría nueva (ni en el carrito ni ya elegida).
    for (const p of sorted) {
      if (chosen.length >= n) break;
      const t = (p.type || '').toLowerCase();
      if (t && (cartTypes.has(t) || usedTypes.has(t))) continue;
      take(p);
    }
    // 2) se permite la categoría del carrito, pero sin repetir entre recomendaciones.
    if (chosen.length < n) {
      for (const p of sorted) {
        if (chosen.length >= n) break;
        if (chosen.includes(p)) continue;
        const t = (p.type || '').toLowerCase();
        if (t && usedTypes.has(t)) continue;
        take(p);
      }
    }
    // 3) relleno con lo que quede, manteniendo el orden.
    if (chosen.length < n) {
      for (const p of sorted) {
        if (chosen.length >= n) break;
        if (!chosen.includes(p)) chosen.push(p);
      }
    }
    return chosen;
  }

  // Carga en caché, cuando el navegador está ocioso, las primeras miniaturas de
  // la colección (las candidatas más habituales), para que las recomendaciones
  // aparezcan al instante al abrir el carrito.
  prefetchRecImages() {
    if (!this.recs || !this.recs.length) return;
    const urls = this.recs.slice(0, 10).map(p => p.img).filter(Boolean);
    const warm = () => urls.forEach(src => { const img = new Image(); img.decoding = 'async'; img.src = src; });
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 2000 });
    } else {
      setTimeout(warm, 1000);
    }
  }

  recsHtml(cart) {
    const picks = this.pickRecs(cart);
    if (!picks.length) return '';
    const items = picks.map(p => `
      <div class="cart-rec">
        <a class="cart-rec__img-link" href="${escapeHtml(p.url)}">${p.img ? `<img class="cart-rec__img" src="${escapeHtml(p.img)}" alt="${escapeHtml(p.title)}" decoding="async" fetchpriority="high">` : ''}</a>
        <div class="cart-rec__meta">
          <p class="cart-rec__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></p>
          ${p.multi
            ? `<a class="cart-rec__add" href="${escapeHtml(p.url)}">${escapeHtml(this.recsLabels.add)}</a>`
            : `<button class="cart-rec__add" type="button" data-variant="${p.id}">${escapeHtml(this.recsLabels.add)}</button>`}
        </div>
        <p class="cart-rec__price">${formatMoney(p.price)}</p>
      </div>`).join('');
    return `
      <div class="cart-recs">
        <p class="cart-recs__title">${escapeHtml(this.recsLabels.title)}</p>
        ${items}
      </div>`;
  }

  async addVariant(variantId, btn) {
    if (btn) btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error('add failed');
      await this.refreshCart();
    } catch (e) {
      console.error('[CartPanel] addVariant error:', e);
      if (btn) btn.disabled = false;
    }
  }

  bindRecs(body) {
    body.querySelectorAll('.cart-rec__add[data-variant]').forEach(btn => {
      btn.addEventListener('click', () => this.addVariant(btn.dataset.variant, btn));
    });
  }

  renderCart(cart) {
    const body = this.panel.querySelector('.cart-panel__body') || this.panel;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <h2 class="cart-panel__title">${escapeHtml(this.labels.title)}</h2>
        <p class="cart-panel__empty">${escapeHtml(this.labels.emptyMessage)}</p>
        ${this.recsHtml(cart)}
        <a href="${escapeHtml(this.labels.continueShoppingUrl)}" class="button-cristal cart-panel__keep-shopping--empty" style="display:block;text-align:center;width:100%;margin-top:0;">
          ${escapeHtml(this.labels.continueShoppingText)}
        </a>
      `;
      this.bindRecs(body);
      return;
    }

    const itemsHtml = cart.items.map(item => {
      const key = escapeHtml(item.key);
      const title = escapeHtml(item.product_title);
      const image = escapeHtml(item.image);
      const variant = escapeHtml(item.variant_title);
      return `
      <li class="cart-panel__item" data-key="${key}">
        <div class="cart-panel__item-main">
          ${item.image ? `<img class="cart-panel__thumb" src="${image}" alt="${title}" loading="lazy">` : ''}
          <div class="cart-panel__item-meta">
            <div class="cart-panel__item-header">
              <p class="cart-panel__item-title">${title}</p>
              <p class="cart-panel__item-price">${formatMoney(item.price)}${item.unit_price_measurement && item.unit_price_measurement.quantity_value > 1 ? ' ' + THEME_STRINGS.setOf + ' ' + Math.floor(item.unit_price_measurement.quantity_value) : ''}</p>
            </div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-panel__item-variant">${variant}</p>` : ''}
            <div class="cart-panel__item-actions">
              <input class="cart-panel__qty-input" type="number" min="1" value="${item.quantity}" data-key="${key}" aria-label="${escapeHtml(THEME_STRINGS.quantity)} ${title}">
            </div>
            <button class="cart-panel__remove" data-key="${key}" aria-label="${escapeHtml(this.labels.removeText)} ${title}">${escapeHtml(this.labels.removeText)}</button>
          </div>
        </div>
      </li>
    `;
    }).join('');

    body.innerHTML = `
      <h2 class="cart-panel__title">${escapeHtml(this.labels.title)}</h2>
      <ul class="cart-panel__list" aria-label="${escapeHtml(THEME_STRINGS.cartItems)}">${itemsHtml}</ul>
      ${this.summaryHtml(cart)}
      <div class="cart-panel__checkout-group">
        ${this.announcementHtml ? `<div class="cart-panel__announcement archivo-richtext">${this.announcementHtml}</div>` : ''}
        <a href="/checkout" class="button-cristal cart-panel__checkout" style="display:block;text-align:center;width:100%;margin-top:var(--space-s);">
          ${escapeHtml(this.labels.checkoutText)}
        </a>
      </div>
      <p class="cart-panel__checkout-note caption">${escapeHtml(this.labels.securePaymentText)}</p>
      ${this.recsHtml(cart)}
      <a href="${escapeHtml(this.labels.continueShoppingUrl)}" class="button-cristal cart-panel__keep-shopping--empty" style="display:block;text-align:center;width:100%;margin-top:var(--space-l);">
        ${escapeHtml(this.labels.continueShoppingText)}
      </a>
    `;

    this.bindRecs(body);

    body.querySelectorAll('.cart-panel__remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
    });

    body.querySelectorAll('.cart-panel__qty-input').forEach(input => {
      input.addEventListener('change', () => {
        const qty = Math.max(1, parseInt(input.value, 10) || 1);
        this.updateQuantity(input.dataset.key, qty);
      });
    });
  }

  async updateQuantity(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refreshCart();
    } catch (err) {
      console.error('[CartPanel] updateQuantity error:', err);
    }
  }

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      await this.refreshCart();
    } catch (err) {
      console.error('[CartPanel] removeItem error:', err);
    }
  }
}

/* ── NewsletterPanel ────────────────────────────────────────── */
class NewsletterPanel {
  constructor() {
    this.panel = document.querySelector('.newsletter-panel');
    this.overlay = document.querySelector('.newsletter-overlay');
    if (!this.panel) return;

    this.form = this.panel.querySelector('form');
    // List ID público de Klaviyo. Fallback duro por si el setting llegara vacío.
    this.listId = this.panel.dataset.klaviyoList || 'S4vgWv';
    // Public API Key (Site ID) de Klaviyo para el aviso "Back in Stock".
    this.companyId = this.panel.dataset.klaviyoCompany || 'VmXmcg';
    // Variante a avisar cuando el panel se abre desde un producto agotado.
    this.bisVariant = null;
    this.bisProduct = '';
    // Guardamos el título/copy originales (modo newsletter) para restaurarlos.
    this.titleEl = this.panel.querySelector('.newsletter-panel__title');
    this.origTitle = this.titleEl ? this.titleEl.textContent : '';
    const copyEl0 = this.panel.querySelector('.newsletter-panel__copy');
    this.origCopy = copyEl0 ? copyEl0.innerHTML : '';
    // Plantillas del modo "back in stock" ({product} = nombre del producto).
    this.bisTitleTpl = this.panel.dataset.bisTitle || '«{product}» está agotado';
    this.bisCopyTpl = this.panel.dataset.bisCopy || 'A veces reaparecen más unidades de estas piezas. Déjanos tu correo y te avisamos en cuanto vuelva a estar disponible.';
    this.bindEvents();

    // Si ya se suscribió en esta sesión, mostrar el estado "gracias" en vez del
    // input (sessionStorage: se limpia al cerrar la pestaña).
    try {
      if (sessionStorage.getItem('newsletter_subscribed') === '1') this.renderSubscribed();
    } catch (_) { /* sessionStorage no disponible */ }

    // Enlace compartible (p. ej. desde Instagram): si la URL trae
    // ?newsletter=open (o #newsletter), abrimos el panel al cargar.
    try {
      var params = new URLSearchParams(window.location.search);
      var wantsNewsletter = window.location.hash === '#newsletter' || params.has('newsletter');
      if (wantsNewsletter) {
        this.bisVariant = null;
        var subscribed = false;
        try { subscribed = sessionStorage.getItem('newsletter_subscribed') === '1'; } catch (_) { /* no-op */ }
        if (!subscribed) this.resetContent();
        this.open();
      }
    } catch (_) { /* no-op */ }
  }

  bindEvents() {
    const closeBtn = this.panel.querySelector('.newsletter-panel__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Intercepta el envío y suscribe directamente a la lista de Klaviyo, que es
    // la que dispara el welcome flow con el código. El form es HTML puro (no de
    // Shopify), así que preventDefault deja la suscripción 100% en Klaviyo.
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.subscribe(e));
    }

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.newsletter-trigger, [data-newsletter-open]');
      if (!trigger) return;
      e.preventDefault();
      // Si el disparador es un botón de producto agotado, guarda su variante y
      // nombre, y pinta el panel en modo "back in stock" (centrado, texto propio).
      this.bisVariant = trigger.dataset.bisVariant || null;
      this.bisProduct = trigger.dataset.bisProduct || '';
      if (this.bisVariant) this.applyBisContent(); else this.resetContent();
      this.open();
    });

    document.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      // Cubre #newsletter, /newsletter y /pages/newsletter (el panel sustituye
      // a la página: el original redirige /newsletter → / y abre el panel).
      const isNewsletter =
        href === '#newsletter' || href.endsWith('#newsletter') ||
        href === '/newsletter' || href.endsWith('/newsletter') ||
        href.endsWith('/pages/newsletter');
      if (isNewsletter) {
        e.preventDefault();
        this.bisVariant = null;
        this.resetContent();
        this.open();
      }
    });
  }

  open() {
    this.panel.classList.add('newsletter-panel--open');
    if (this.overlay) this.overlay.classList.add('newsletter-overlay--open');
  }

  close() {
    this.panel.classList.remove('newsletter-panel--open');
    if (this.overlay) this.overlay.classList.remove('newsletter-overlay--open');
    this.bisVariant = null;
  }

  // Modo "back in stock": centra el panel y pone título/copy propios del producto.
  applyBisContent() {
    this.panel.classList.add('newsletter-panel--bis');
    // El back in stock es por producto: muestra el formulario aunque ya se hubiera
    // suscrito al newsletter en esta sesión, y limpia mensajes anteriores.
    const row = this.form.querySelector('.newsletter-form__row');
    const legal = this.form.querySelector('.newsletter-form__legal');
    if (row) row.style.display = '';
    if (legal) legal.style.display = '';
    this.form.querySelectorAll('.newsletter-form__success, .newsletter-form__error').forEach((el) => el.remove());
    const product = this.bisProduct || '';
    if (this.titleEl) this.titleEl.textContent = this.bisTitleTpl.replace(/\{product\}/g, product);
    let copy = this.panel.querySelector('.newsletter-panel__copy');
    if (!copy) {
      copy = document.createElement('p');
      copy.className = 'newsletter-panel__copy';
      copy.dataset.bisTemp = '1';
      if (this.titleEl) this.titleEl.insertAdjacentElement('afterend', copy);
    }
    copy.style.display = '';
    copy.textContent = this.bisCopyTpl.replace(/\{product\}/g, product);
  }

  // Restaura el panel a modo newsletter normal.
  resetContent() {
    this.panel.classList.remove('newsletter-panel--bis');
    if (this.titleEl) this.titleEl.textContent = this.origTitle;
    const copy = this.panel.querySelector('.newsletter-panel__copy');
    if (copy) {
      if (copy.dataset.bisTemp) copy.remove();
      else copy.innerHTML = this.origCopy;
    }
  }

  async subscribe(e) {
    e.preventDefault();

    // Honeypot anti-bots: un humano deja "website" vacío; un bot lo rellena.
    // Si llega con contenido, fingimos éxito sin suscribir (no avisamos al bot).
    const hp = this.form.querySelector('input[name="website"]');
    if (hp && hp.value.trim() !== '') {
      this.markSubscribed();
      return;
    }

    const emailInput = this.form.querySelector('input[type="email"]');
    const email = emailInput ? emailInput.value.trim() : '';
    if (!email) return;

    // Back in Stock: si el panel se abrió desde un producto agotado, además del
    // newsletter registramos el aviso de esa variante en Klaviyo (independiente:
    // si falla, la suscripción al newsletter sigue su curso).
    if (this.bisVariant) this.subscribeBackInStock(email, this.bisVariant);

    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    const errMsg = this.panel.dataset.msgError || 'No hemos podido completar la suscripción. Inténtalo de nuevo.';

    // Suscripción vía el endpoint de listas de Klaviyo (kmail-lists): respeta el
    // double opt-in de la lista y dispara el email de confirmación con el código.
    const body = new URLSearchParams();
    body.append('g', this.listId);
    body.append('email', email);
    body.append('$fields', '');
    body.append('$source', 'Cristal Antiguo — panel newsletter');

    // Timeout: si un ad-blocker o la red retienen la petición a Klaviyo, no
    // dejamos el botón colgado para siempre; abortamos y mostramos error.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);
    try {
      const res = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'cache-control': 'no-cache',
        },
        body: body.toString(),
        signal: ctrl.signal,
      });
      const data = await res.json().catch(() => ({}));
      const ok = res.ok && data.success !== false && !(data.errors && data.errors.length);

      if (ok) {
        this.markSubscribed();
      } else {
        const msg = (data.errors && data.errors[0]) || errMsg;
        this.showMessage('error', msg);
      }
    } catch (err) {
      this.showMessage('error', errMsg);
    } finally {
      clearTimeout(timer);
      if (button) button.disabled = false;
    }
  }

  // Registra un aviso "Back in Stock" en Klaviyo para una variante concreta.
  // Endpoint cliente (sin backend): cuando la variante vuelve a tener stock, el
  // flow "Back in Stock" de Klaviyo envía el correo. Silencioso: no bloquea el
  // newsletter ni molesta al usuario si falla.
  async subscribeBackInStock(email, variantId) {
    if (!this.companyId || !variantId) return;
    try {
      await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(this.companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId },
              },
            },
          },
        }),
      });
    } catch (err) {
      console.error('[BackInStock] error:', err);
    }
  }

  // Marca la suscripción como hecha en esta sesión y pinta el estado "gracias".
  markSubscribed() {
    try { sessionStorage.setItem('newsletter_subscribed', '1'); } catch (_) { /* no-op */ }
    this.renderSubscribed();
  }

  // Oculta el input/botón y deja solo el mensaje de agradecimiento.
  renderSubscribed() {
    const row = this.form.querySelector('.newsletter-form__row');
    const legal = this.form.querySelector('.newsletter-form__legal');
    if (row) row.style.display = 'none';
    if (legal) legal.style.display = 'none';
    const okMsg = this.bisVariant
      ? (this.panel.dataset.bisMsgOk || 'Te avisaremos por correo en cuanto vuelva a estar disponible.')
      : (this.panel.dataset.msgSubscribed || '¡Gracias por tu suscripción! Revisa tu bandeja de correo.');
    this.showMessage('success', okMsg);
  }

  showMessage(type, text) {
    this.form.querySelectorAll('.newsletter-form__success, .newsletter-form__error').forEach((el) => el.remove());
    const p = document.createElement('p');
    p.className = type === 'success' ? 'newsletter-form__success' : 'newsletter-form__error';
    p.setAttribute('role', 'status');
    p.textContent = text;
    this.form.appendChild(p);
  }
}

/* ── AnnouncementBar ────────────────────────────────────────── */
class AnnouncementBar {
  constructor() {
    this.bar = document.querySelector('.cristal-announcement-bar');
    const root = document.documentElement;

    if (!this.bar) {
      root.style.setProperty('--announcement-bar-height', '0px');
      return;
    }

    if (sessionStorage.getItem('announcement_closed') === '1') {
      this.bar.style.display = 'none';
      root.style.setProperty('--announcement-bar-height', '0px');
      return;
    }

    const updateHeight = () => {
      root.style.setProperty('--announcement-bar-height', this.bar.offsetHeight + 'px');
    };

    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateHeight).observe(this.bar);
    }

    const closeBtn = this.bar.querySelector('.cristal-announcement-bar__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        sessionStorage.setItem('announcement_closed', '1');
        this.bar.style.display = 'none';
        root.style.setProperty('--announcement-bar-height', '0px');
      });
    }
  }
}

/* ── PopupBanner ────────────────────────────────────────────── */
class PopupBanner {
  constructor() {
    this.overlay = document.getElementById('popup-overlay');
    this.banner = document.getElementById('popup-banner');
    if (!this.banner) return;

    const dias = parseInt(this.banner.dataset.popupDias || '7', 10);
    const delayMs = parseInt(this.banner.dataset.popupDelay || '3', 10) * 1000;
    const STORAGE_KEY = 'ca_popup_last_shown';

    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const last = parseInt(raw, 10);
      const daysSince = (Date.now() - last) / (1000 * 60 * 60 * 24);
      if (daysSince < dias) return;
    }

    const closeBtn = this.banner.querySelector('.popup-banner__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    window.setTimeout(() => this.show(), Math.max(0, delayMs));
  }

  show() {
    if (this.overlay) {
      this.overlay.hidden = false;
      this.overlay.classList.add('popup-overlay--open');
    }
    if (this.banner) {
      this.banner.hidden = false;
      this.banner.classList.add('popup-banner--open');
    }
    document.body.style.overflow = 'hidden';
  }

  close() {
    localStorage.setItem('ca_popup_last_shown', String(Date.now()));
    if (this.overlay) {
      this.overlay.classList.remove('popup-overlay--open');
      this.overlay.hidden = true;
    }
    if (this.banner) {
      this.banner.classList.remove('popup-banner--open');
      this.banner.hidden = true;
    }
    document.body.style.overflow = '';
  }
}

/* ── CookieBanner ───────────────────────────────────────────── */
class CookieBanner {
  constructor() {
    this.CONSENT_VERSION = 2;
    this.COOKIE_NAME = 'ca_cookie_consent';

    this.banner = document.querySelector('.cristal-cookie-banner');
    if (!this.banner) return;

    this.bannerView = this.banner.querySelector('.cristal-cookie-banner__main');
    this.prefsView = this.banner.querySelector('.cristal-cookie-preferences');

    // Siempre enlazamos eventos: el botón «Configurar cookies» del footer debe
    // poder reabrir el panel aunque ya exista consentimiento guardado.
    this.bindEvents();

    const saved = this.readConsent();
    if (saved && saved.version === this.CONSENT_VERSION) {
      this.applyConsent(saved);
      return;
    }

    this.showBanner();
  }

  readConsent() {
    try {
      const val = document.cookie.split('; ').find(r => r.startsWith(this.COOKIE_NAME + '='));
      if (!val) return null;
      return JSON.parse(decodeURIComponent(val.split('=')[1]));
    } catch {
      return null;
    }
  }

  writeConsent(data) {
    const json = encodeURIComponent(JSON.stringify(data));
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${this.COOKIE_NAME}=${json}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  showBanner() {
    this.banner.classList.add('cristal-cookie-banner--visible');
    if (this.bannerView) this.bannerView.style.display = 'block';
    if (this.prefsView) this.prefsView.style.display = 'none';
  }

  showPreferences() {
    // Pre-rellena los checkboxes con el consentimiento guardado (si lo hay).
    const saved = this.readConsent();
    const analyticsCheck = this.banner.querySelector('[data-cookie-analytics]');
    const marketingCheck = this.banner.querySelector('[data-cookie-marketing]');
    if (analyticsCheck) analyticsCheck.checked = saved ? !!saved.analytics : false;
    if (marketingCheck) marketingCheck.checked = saved ? !!saved.marketing : false;

    if (this.bannerView) this.bannerView.style.display = 'none';
    if (this.prefsView) {
      this.prefsView.style.display = 'block';
      this.prefsView.classList.add('cristal-cookie-preferences--visible');
    }
  }

  openPreferences() {
    this.banner.classList.add('cristal-cookie-banner--visible');
    this.showPreferences();
  }

  hideBanner() {
    this.banner.classList.remove('cristal-cookie-banner--visible');
  }

  bindEvents() {
    // Delegación: los botones «Aceptar/Rechazar todo» existen en las DOS vistas
    // (banner y preferencias), así que no sirve querySelector (solo el primero).
    this.banner.addEventListener('click', (e) => {
      if (e.target.closest('[data-cookie-accept]')) this.acceptAll();
      else if (e.target.closest('[data-cookie-reject]')) this.rejectAll();
      else if (e.target.closest('[data-cookie-configure]')) this.showPreferences();
      else if (e.target.closest('[data-cookie-save]')) this.savePreferences();
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-open-cookie-settings]');
      if (!btn) return;
      e.preventDefault();
      this.openPreferences();
    });
  }

  acceptAll() {
    const consent = { version: this.CONSENT_VERSION, analytics: true, marketing: true };
    this.writeConsent(consent);
    this.applyConsent(consent);
    this.hideBanner();
  }

  rejectAll() {
    const consent = { version: this.CONSENT_VERSION, analytics: false, marketing: false };
    this.writeConsent(consent);
    this.applyConsent(consent);
    this.hideBanner();
  }

  savePreferences() {
    const analyticsCheck = this.banner.querySelector('[data-cookie-analytics]');
    const marketingCheck = this.banner.querySelector('[data-cookie-marketing]');
    const consent = {
      version: this.CONSENT_VERSION,
      analytics: analyticsCheck ? analyticsCheck.checked : false,
      marketing: marketingCheck ? marketingCheck.checked : false,
    };
    this.writeConsent(consent);
    this.applyConsent(consent);
    this.hideBanner();
  }

  applyConsent(consent) {
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
      });
    }

    if (consent.analytics) this.loadGA4();
    if (consent.marketing) this.loadMetaPixel();
  }

  loadGA4() {
    if (window._ga4Loaded) return;
    window._ga4Loaded = true;

    const GA4_ID = document.documentElement.dataset.ga4Id;
    if (!GA4_ID) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    gtag('config', GA4_ID);
  }

  loadMetaPixel() {
    if (window._metaPixelLoaded) return;
    window._metaPixelLoaded = true;

    const PIXEL_ID = document.documentElement.dataset.metaPixelId;
    if (!PIXEL_ID) return;

    !function(f,b,e,v,n,t,s){
      if(f.fbq) return; n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0';
      n.queue=[]; t=b.createElement(e); t.async=!0;
      t.src=v; s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)
    }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');

    fbq('init', PIXEL_ID);
    fbq('track', 'PageView');
  }
}

/* ── Infinite slider helper ─────────────────────────────────── */
function initInfiniteSlider({ track, slideSelector, counterEl, prevBtn, nextBtn, swipeEl }) {
  const origSlides = [...track.querySelectorAll(slideSelector)];
  const total = origSlides.length;
  if (total === 0) return;

  let current = 0;

  if (total > 1) {
    const lastClone = origSlides[total - 1].cloneNode(true);
    const firstClone = origSlides[0].cloneNode(true);
    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.setAttribute('aria-hidden', 'true');
    track.insertBefore(lastClone, origSlides[0]);
    track.appendChild(firstClone);
    current = 1;
  }

  function setPos(index, animate) {
    current = index;
    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translateX(-${index * 100}%)`;
    if (counterEl) {
      const real = total > 1 ? ((index - 1 + total) % total) : 0;
      counterEl.textContent = `${real + 1}/${total}`;
    }
  }

  // Init without animation
  setPos(current, false);

  // After wrap-around transition: silently jump to real position
  track.addEventListener('transitionend', () => {
    if (total <= 1) return;
    if (current >= total + 1) {
      setPos(1, false);
      void track.offsetWidth; // force reflow so next transition works
    } else if (current <= 0) {
      setPos(total, false);
      void track.offsetWidth;
    }
  });

  function prev() { setPos(current - 1, true); }
  function next() { setPos(current + 1, true); }

  if (prevBtn) prevBtn.addEventListener('click', prev);
  if (nextBtn) nextBtn.addEventListener('click', next);

  if (swipeEl) {
    let startX = 0;
    swipeEl.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    swipeEl.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    }, { passive: true });
  }
}

/* ── ProductGallery ─────────────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    const track = el.querySelector('.cristal-product-gallery__track');
    if (!track) return;
    initInfiniteSlider({
      track,
      slideSelector: '.cristal-product-gallery__slide',
      counterEl: el.querySelector('.cristal-product-gallery__counter'),
      prevBtn: el.querySelector('.cristal-product-gallery__nav--prev'),
      nextBtn: el.querySelector('.cristal-product-gallery__nav--next'),
      swipeEl: el,
    });
  }
}

/* ── ArchivoSlider ──────────────────────────────────────────── */
class ArchivoSlider {
  constructor(el) {
    const track = el.querySelector('.archivo-slider__track');
    if (!track) return;
    initInfiniteSlider({
      track,
      slideSelector: '.archivo-slider__slide',
      counterEl: el.querySelector('.archivo-slider__counter'),
      prevBtn: el.querySelector('.archivo-slider__nav--prev'),
      nextBtn: el.querySelector('.archivo-slider__nav--next'),
      swipeEl: el,
    });
  }
}

/* ── Variant selector (product page) ───────────────────────── */
function initVariantSelector() {
  const form = document.querySelector('[data-add-to-cart-form]');
  if (!form) return;

  const selects = form.querySelectorAll('.cristal-variant-select');
  if (!selects.length) return;

  const variantData = JSON.parse(
    document.getElementById('product-variants-json')?.textContent || '[]'
  );

  function findVariant() {
    const selected = Array.from(selects).map(s => s.value);
    return variantData.find(v =>
      v.options.every((opt, i) => opt === selected[i])
    );
  }

  function updateForm() {
    const variant = findVariant();
    const idInput = form.querySelector('input[name="id"]');
    const submitBtn = form.querySelector('[type="submit"]');
    const priceEl = document.querySelector('.cristal-product-detail__price');

    if (!variant) return;

    if (idInput) idInput.value = variant.id;

    if (submitBtn) {
      if (variant.available) {
        submitBtn.disabled = false;
        submitBtn.textContent = THEME_STRINGS.addToCart;
      } else {
        submitBtn.disabled = true;
        submitBtn.textContent = THEME_STRINGS.soldOut;
      }
    }

    if (priceEl) {
      // Labels desde el bloque (data-attrs, traducibles); fallback a locales.
      const priceLabel = priceEl.dataset.priceLabel || THEME_STRINGS.priceLabel;
      const soldLabel = priceEl.dataset.soldLabel || THEME_STRINGS.soldOut;
      if (variant.available && variant.price != null) {
        const price = (variant.price / 100).toFixed(2).replace('.', ',') + ' €';
        let html = escapeHtml(priceLabel) + ' ' + price;
        const upm = variant.unit_price_measurement;
        if (upm && parseFloat(upm.quantity_value) > 1) {
          html += ' ' + escapeHtml(THEME_STRINGS.setOf) + ' ' + Math.floor(parseFloat(upm.quantity_value));
        }
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const comparePrice = (variant.compare_at_price / 100).toFixed(2).replace('.', ',') + ' €';
          html += ` <span class="cristal-product-detail__price-compare">${comparePrice}</span>`;
        }
        priceEl.innerHTML = html;
      } else {
        priceEl.innerHTML = `<span class="cristal-product-detail__sold">${escapeHtml(soldLabel)}</span>`;
      }
    }
  }

  selects.forEach(s => s.addEventListener('change', updateForm));
}

/* ── Collection filter ──────────────────────────────────────── */
function initCollectionFilter() {
  const filterBtns = document.querySelectorAll('.cristal-filter-btn');
  if (!filterBtns.length) return;

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const categoria = btn.dataset.categoria || '';
      const url = new URL(window.location.href);
      if (categoria) {
        url.searchParams.set('categoria', categoria);
      } else {
        url.searchParams.delete('categoria');
      }
      window.location.href = url.toString();
    });
  });

  const params = new URLSearchParams(window.location.search);
  const active = params.get('categoria') || '';
  filterBtns.forEach(btn => {
    const cat = btn.dataset.categoria || '';
    btn.classList.toggle('cristal-filter-btn--active', cat === active);
  });
}

/* ── Floating logo ──────────────────────────────────────────── */
/* Ancla el logo justo encima del footer (bottom = altura del footer − 40px),
   igual que el componente FloatingLogo del original. */
function initFloatingLogo() {
  const logo = document.querySelector('.cristal-floating-logo');
  const footer = document.querySelector('.cristal-footer');
  if (!logo || !footer) return;

  const update = () => {
    const h = footer.offsetHeight;
    document.documentElement.style.setProperty('--logo-bottom', (h - 40) + 'px');
  };

  update();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(update).observe(footer);
  }
  window.addEventListener('resize', update);
}

function initHeaderSlots() {
  document.querySelectorAll('.cristal-header-inner').forEach((inner) => {
    const start = inner.querySelector('.cristal-header-slot--start');
    const end = inner.querySelector('.cristal-header-slot--end');
    if (!start || !end) return;

    inner.querySelectorAll('.cristal-header-action').forEach((action) => {
      if (start.contains(action) || end.contains(action)) return;

      const isRight = action.dataset.headerSide === 'right'
        || action.querySelector('.cristal-header-side--end, .cart-trigger.cristal-header-side--end');

      (isRight ? end : start).appendChild(action);
    });
  });
}

/* ── Preferencias de cookies (banner nativo de Shopify) ─────────
   El enlace «Configurar Cookies» del footer reabre las preferencias nativas
   (window.privacyBanner.showPreferences). El banner propio está desactivado. */
function initNativeCookiePrefs() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-open-cookie-settings]');
    if (!btn) return;
    e.preventDefault();
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      window.privacyBanner.showPreferences();
    }
  });
}

/* ── Init ───────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  // Cada init aislado: si uno peta, no debe tumbar a los siguientes (antes un
  // fallo en AnnouncementBar/CartPanel dejaba el interceptor de la newsletter
  // sin engancharse -> el form caía al submit nativo y recargaba).
  const safe = (fn) => { try { fn(); } catch (e) { console.error('[init]', e); } };

  safe(() => new NewsletterPanel());
  safe(() => new AnnouncementBar());
  safe(() => new CartPanel());
  safe(() => new PopupBanner());
  safe(() => new CookieBanner());

  safe(() => document.querySelectorAll('.cristal-product-gallery').forEach(el => new ProductGallery(el)));
  safe(() => document.querySelectorAll('.archivo-slider').forEach(el => new ArchivoSlider(el)));

  safe(initVariantSelector);
  safe(initCollectionFilter);
  safe(initFloatingLogo);
  safe(initHeaderSlots);
  safe(initNativeCookiePrefs);
});

document.addEventListener('shopify:section:load', (e) => {
  initHeaderSlots(e);
  e.target.querySelectorAll('.archivo-slider').forEach(el => new ArchivoSlider(el));
});
document.addEventListener('shopify:block:select', initHeaderSlots);
